/**
 * Shaping text into a glyph run (ADR 3, decisions 30 and 31): glyph IDs and advances in font units with kerning
 * and standard ligatures from the face's tables, each glyph mapped back to the text it stands for (`clusters.ts`),
 * in flat typed arrays the layout worker can keep and transfer. Tables S (pieces), F (fallbacks) and K (small
 * caps) in the part 12 log.
 *
 * Default-ignorable code points (the soft hyphen, joiners, variation selectors, …) are never shaped: fontkit
 * would swap each for a space glyph whose code points no longer match the text. They end a piece instead, so
 * nothing ligates or kerns across them (a zero-width non-joiner keeps "f‌i" apart, as the writer meant), and
 * they draw nothing: their offsets fall in the cluster of the glyph before them.
 */
import { at } from '../arrays.ts';
import type { BookLanguage } from '../theme/metadata.ts';
import { clusterOffsets, ShapingError } from './clusters.ts';
import type { FaceMetrics, Features, RawRun, ShapingFace } from './face.types.ts';
import { OPENTYPE_LANGUAGES } from './faces.ts';
import { decode, encode, isDefaultIgnorable } from './text.ts';
import type { Decoded } from './text.ts';

/**
 * A shaped run: one entry per glyph in every array. Advances and offsets are in font units of the face, already
 * multiplied by the glyph's scale; draw a glyph's outline at `scales[i]` times the type size.
 */
export interface GlyphRun {
  readonly glyphIds: Uint16Array<ArrayBuffer>;
  /** UTF-16 offset in the text where the glyph's cluster starts; never decreasing, the first one 0. */
  readonly clusters: Uint32Array<ArrayBuffer>;
  readonly advances: Float64Array<ArrayBuffer>;
  readonly xOffsets: Float64Array<ArrayBuffer>;
  readonly yOffsets: Float64Array<ArrayBuffer>;
  /** 1, or the synthetic small-caps scale for a capital drawn in place of a lowercase letter (table K). */
  readonly scales: Float64Array<ArrayBuffer>;
}

/** How to shape: true small caps (`^^small caps^^`, decision 35) and the book's language (local forms). */
export interface ShapeOptions {
  readonly smallCaps: boolean;
  readonly language: BookLanguage | null;
}

/** The features every run shapes with, on top of fontkit's defaults for the script (decision 30). */
export const BASE_FEATURES: Features = Object.freeze({ kern: true, liga: true });

/** Scale for synthetic small caps when a face's metrics don't give one: a common typographic default. */
export const FALLBACK_SMALL_CAPS_SCALE = 0.7;

/**
 * The scale of a synthetic small capital (K3): a capital shrunk to the face's x-height, so it lines up with the
 * lowercase letters beside it. Faces whose metrics can't say get {@link FALLBACK_SMALL_CAPS_SCALE}.
 */
export function smallCapsScale(metrics: FaceMetrics): number {
  const { capHeight, xHeight } = metrics;
  return xHeight > 0 && capHeight > xHeight ? xHeight / capHeight : FALLBACK_SMALL_CAPS_SCALE;
}

/** A piece of text shaped in one call: its code points with their offsets in the whole text, and its scale. */
interface Piece {
  readonly text: string;
  readonly source: Decoded;
  readonly start: number;
  readonly features: Features;
  readonly scale: number;
}

/** A segment after table M: what is shaped, and the advance each substituted space takes, by its offset. */
interface Substituted {
  readonly source: Decoded;
  readonly widths: ReadonlyMap<number, number>;
}

/** A shaped piece: glyphs with their cluster starts in the whole text. */
interface ShapedPiece {
  readonly run: RawRun;
  readonly clusters: readonly number[];
  readonly scale: number;
}

function slice(decoded: Decoded, from: number, to: number): Decoded {
  return { codePoints: decoded.codePoints.slice(from, to), offsets: decoded.offsets.slice(from, to) };
}

/** S1: the runs of code points between default ignorables (empty ones shape to nothing, T3b and T7 included). */
function segments(decoded: Decoded): Decoded[] {
  const parts: Decoded[] = [];
  let from = 0;
  decoded.codePoints.forEach((codePoint, i) => {
    if (!isDefaultIgnorable(codePoint)) return;
    parts.push(slice(decoded, from, i));
    from = i + 1;
  });
  parts.push(slice(decoded, from, decoded.codePoints.length));
  return parts;
}

/** A combining mark (Unicode general category M). */
const COMBINING_MARK = /^\p{M}$/u;

/** A blank: where F2 splits words and T7 prefers to cut. */
const BLANK = /\s/u;

/** Whether text holds a combining mark anywhere. */
const HAS_MARK = /\p{M}/u;

/**
 * K2, K7: a segment for small caps, split where it changes between letters drawn as synthetic small capitals
 * (`capital` says which: shaped as capitals, scaled, without `smcp`) and everything else (shaped as written, with
 * `rest`'s features). A capital form of several code points ("ß" → "SS") maps each of them to the letter's offset,
 * so they share its cluster.
 */
function caseRuns(segment: Decoded, capital: (codePoint: number) => boolean, rest: Features, scale: number): Piece[] {
  const runs: { scaled: boolean; codePoints: number[]; offsets: number[] }[] = [];
  segment.codePoints.forEach((codePoint, i) => {
    const written = String.fromCodePoint(codePoint);
    let run = runs.at(-1);
    // K6: a combining mark goes with the letter before it, so an accent stays on its (scaled) capital.
    const scaled = run !== undefined && COMBINING_MARK.test(written) ? run.scaled : capital(codePoint);
    if (run?.scaled !== scaled) {
      run = { scaled, codePoints: [], offsets: [] };
      runs.push(run);
    }
    for (const unit of scaled ? decode(written.toUpperCase()).codePoints : [codePoint]) {
      run.codePoints.push(unit);
      run.offsets.push(at(segment.offsets, i));
    }
  });
  return runs.map((run) => ({
    text: encode(run.codePoints),
    source: { codePoints: run.codePoints, offsets: run.offsets },
    start: at(run.offsets, 0),
    features: run.scaled ? BASE_FEATURES : rest,
    scale: run.scaled ? scale : 1,
  }));
}

/** K2, K8: whether a code point has a capital form other than itself that the face can draw entirely. */
function hasCapital(face: ShapingFace, codePoint: number): boolean {
  const written = String.fromCodePoint(codePoint);
  const upper = written.toUpperCase();
  return upper !== written && decode(upper).codePoints.every((part) => face.hasGlyph(part));
}

const smallCapsCache = new WeakMap<ShapingFace, Map<string, boolean>>();

/**
 * K7: whether the face's `smcp` turns a letter into another glyph in the language shaped (a language's local forms
 * can swap the letter for one `smcp` doesn't cover), asked once per face, language and letter: the letter alone, with
 * and without it. A shaper that throws on the letter counts as no.
 */
function smallCapsCovers(face: ShapingFace, codePoint: number, language: string | null): boolean {
  let known = smallCapsCache.get(face);
  if (known === undefined) {
    known = new Map();
    smallCapsCache.set(face, known);
  }
  const key = `${language ?? ''} ${codePoint}`;
  let covers = known.get(key);
  if (covers === undefined) {
    const text = String.fromCodePoint(codePoint);
    const ids = (features: Features): string => face.shape(text, features, language).glyphs.map((glyph) => glyph.id).join(' ');
    try {
      covers = ids({ ...BASE_FEATURES, smcp: true }) !== ids(BASE_FEATURES);
    } catch {
      covers = false;
    }
    known.set(key, covers);
  }
  return covers;
}

/** The space separators of M2 and the advance each takes, from the face's em, space, figure zero and full stop. */
const SPACE_WIDTHS: ReadonlyMap<number, (em: number, advance: (codePoint: number) => number) => number> = new Map([
  [0x00a0, (_em: number, advance: (codePoint: number) => number) => advance(0x20)],
  [0x2000, (em: number) => em / 2],
  [0x2001, (em: number) => em],
  [0x2002, (em: number) => em / 2],
  [0x2003, (em: number) => em],
  [0x2004, (em: number) => em / 3],
  [0x2005, (em: number) => em / 4],
  [0x2006, (em: number) => em / 6],
  [0x2007, (_em: number, advance: (codePoint: number) => number) => advance(0x30)],
  [0x2008, (_em: number, advance: (codePoint: number) => number) => advance(0x2e)],
  [0x2009, (em: number) => em / 5],
  [0x200a, (em: number) => em / 16],
  [0x202f, (_em: number, advance: (codePoint: number) => number) => advance(0x20) / 2],
  [0x205f, (em: number) => (em * 4) / 18],
  [0x3000, (em: number) => em],
]);

/** The longest unit (a base and its marks) M7 and M8 compose and the shaper sees (T2, T3): far past any real letter's marks. */
export const MAX_COMPOSED_UNIT = 32;

/** The longest text one shaper call sees (T7): fontkit's cost is quadratic in a call's length on several paths. */
export const MAX_SHAPER_CALL = 256;

/** M3: the hyphens to try, in order, for a hyphen the face lacks. */
const HYPHENS: ReadonlyMap<number, readonly number[]> = new Map([
  [0x2011, [0x2010, 0x2d]],
  [0x2010, [0x2d]],
]);

/** M3, M4: what to shape in place of a code point the face lacks, or undefined for M5 (its `.notdef`). */
function replacement(face: ShapingFace, codePoint: number): readonly number[] | undefined {
  const hyphen = HYPHENS.get(codePoint)?.find((candidate) => face.hasGlyph(candidate));
  if (hyphen !== undefined) return [hyphen];
  const written = String.fromCodePoint(codePoint);
  for (const form of ['NFD', 'NFKD'] as const) {
    // A code point with no decomposition gives itself back, which the face lacks, so it never passes.
    const decomposed = decode(written.normalize(form)).codePoints;
    if (decomposed.every((part) => face.hasGlyph(part))) return decomposed;
  }
  return undefined;
}

/** Table M per code point (M1–M5): what to shape for one code point, and its advance if it is a resized space (M2). */
function codePointForm(face: ShapingFace, codePoint: number, advance: (codePoint: number) => number): { readonly shaped: readonly number[]; readonly width?: number } {
  if (face.hasGlyph(codePoint)) return { shaped: [codePoint] };
  const width = SPACE_WIDTHS.get(codePoint);
  if (width !== undefined) return { shaped: [0x20], width: width(face.metrics.unitsPerEm, advance) };
  return { shaped: replacement(face, codePoint) ?? [codePoint] };
}

/**
 * M7, M8: a base with its marks composed as far as the face can draw: the composition of the base with its first
 * marks (longest first) and the index in the unit where the marks left after it start; undefined for M9.
 */
function composedForm(face: ShapingFace, unit: readonly number[]): { readonly letter: readonly number[]; readonly rest: number } | undefined {
  // M9: each try normalises a prefix, so a run of marks longer than any letter carries is not composed at all.
  if (unit.length > MAX_COMPOSED_UNIT) return undefined;
  for (let rest = unit.length; rest > 1; rest -= 1) {
    const letter = decode(encode(unit.slice(0, rest)).normalize('NFC')).codePoints;
    if (letter.length < rest && [...letter, ...unit.slice(rest)].every((codePoint) => face.hasGlyph(codePoint))) return { letter, rest };
  }
  return undefined;
}

/**
 * Table M: the segment as the face can draw it. Units are a code point with the combining marks after it; each
 * replacement takes the offset of what it replaced (M6).
 */
function substitute(face: ShapingFace, segment: Decoded): Substituted {
  const text = encode(segment.codePoints);
  if (!HAS_MARK.test(text) && segment.codePoints.every((codePoint) => face.hasGlyph(codePoint))) return { source: segment, widths: new Map() };
  const codePoints: number[] = [];
  const offsets: number[] = [];
  const widths = new Map<number, number>();
  const advance = (codePoint: number): number => at(face.plain(String.fromCodePoint(codePoint)).positions, 0).xAdvance;
  let from = 0;
  while (from < segment.codePoints.length) {
    let to = from + 1;
    while (to < segment.codePoints.length && COMBINING_MARK.test(String.fromCodePoint(at(segment.codePoints, to)))) to += 1;
    const unit = segment.codePoints.slice(from, to);
    const composed = composedForm(face, unit);
    if (composed !== undefined) {
      // M7, M8: the composed letter at the base's offset; the marks left after it keep their own.
      for (const codePoint of composed.letter) {
        codePoints.push(codePoint);
        offsets.push(at(segment.offsets, from));
      }
      for (let i = from + composed.rest; i < to; i += 1) {
        codePoints.push(at(segment.codePoints, i));
        offsets.push(at(segment.offsets, i));
      }
    } else {
      for (let i = from; i < to; i += 1) {
        const offset = at(segment.offsets, i);
        const form = codePointForm(face, at(segment.codePoints, i), advance);
        if (form.width !== undefined) widths.set(offset, form.width);
        for (const part of form.shaped) {
          codePoints.push(part);
          offsets.push(offset);
        }
      }
    }
    from = to;
  }
  return { source: { codePoints, offsets }, widths };
}

function checkedRun(run: RawRun, metrics: FaceMetrics): RawRun {
  if (run.positions.length !== run.glyphs.length) throw new ShapingError(`${run.glyphs.length} glyphs but ${run.positions.length} placements`);
  for (const glyph of run.glyphs) {
    if (!Number.isInteger(glyph.id) || glyph.id < 0 || glyph.id >= metrics.numGlyphs) throw new ShapingError(`glyph ${String(glyph.id)} is not in the face`);
  }
  for (const p of run.positions) {
    if (!Number.isFinite(p.xAdvance) || !Number.isFinite(p.xOffset) || !Number.isFinite(p.yOffset)) throw new ShapingError('a placement is not a number');
  }
  return run;
}

function mapped(run: RawRun, piece: Piece, metrics: FaceMetrics): ShapedPiece {
  const checked = checkedRun(run, metrics);
  return { run: checked, clusters: clusterOffsets(piece.source, piece.start, checked.glyphs.map((g) => g.codePoints)), scale: piece.scale };
}

/** F3: the piece through the character map alone, which can't fail on a face that loaded. */
function plainPiece(face: ShapingFace, piece: Piece): ShapedPiece {
  return mapped(face.plain(piece.text), piece, face.metrics);
}

/** F2: the piece's words and blanks shaped one by one, each word falling back to F3 on its own. */
function wordPieces(face: ShapingFace, piece: Piece, language: string | null): ShapedPiece[] {
  const shaped: ShapedPiece[] = [];
  let from = 0;
  const { codePoints } = piece.source;
  const blank = (i: number): boolean => BLANK.test(String.fromCodePoint(at(codePoints, i)));
  for (let i = 1; i <= codePoints.length; i += 1) {
    if (i < codePoints.length && blank(i) === blank(from)) continue;
    const source = slice(piece.source, from, i);
    const text = encode(source.codePoints);
    const word: Piece = { ...piece, text, source, start: at(source.offsets, 0) };
    try {
      shaped.push(mapped(face.shape(text, word.features, language), word, face.metrics));
    } catch {
      shaped.push(plainPiece(face, word));
    }
    from = i;
  }
  return shaped;
}

/**
 * T7: a piece cut into calls of at most `MAX_SHAPER_CALL` code points: each cut after the last blank in its window,
 * else before the last code point in it that isn't a mark, else at the window's end.
 */
function calls(piece: Piece): Piece[] {
  const { codePoints } = piece.source;
  const isAt = (pattern: RegExp, i: number): boolean => pattern.test(String.fromCodePoint(at(codePoints, i)));
  const parts: Piece[] = [];
  let from = 0;
  while (from < codePoints.length) {
    let to = Math.min(from + MAX_SHAPER_CALL, codePoints.length);
    if (to < codePoints.length) {
      let cut = to;
      while (cut > from + 1 && !isAt(BLANK, cut - 1)) cut -= 1;
      if (cut === from + 1) {
        cut = to;
        while (cut > from + 1 && isAt(COMBINING_MARK, cut)) cut -= 1;
      }
      to = cut;
    }
    const source = slice(piece.source, from, to);
    parts.push({ ...piece, text: encode(source.codePoints), source, start: at(source.offsets, 0) });
    from = to;
  }
  return parts;
}

/** F1: the piece shaped whole; when the shaper throws or returns what can't be mapped, F2. */
function shapePiece(face: ShapingFace, piece: Piece, language: string | null): ShapedPiece[] {
  try {
    return [mapped(face.shape(piece.text, piece.features, language), piece, face.metrics)];
  } catch {
    return wordPieces(face, piece, language);
  }
}

/**
 * T3: the segment cut where it changes between code points the shaper takes and marks past the first
 * `MAX_COMPOSED_UNIT - 1` after one base (fontkit places each mark by scanning back over every mark since its base),
 * which are drawn through the character map alone (`plain`).
 */
function boundedMarks(segment: Decoded): { readonly source: Decoded; readonly plain: boolean }[] {
  let marks = 0;
  const excess = segment.codePoints.map((codePoint) => {
    marks = COMBINING_MARK.test(String.fromCodePoint(codePoint)) ? marks + 1 : 0;
    return marks >= MAX_COMPOSED_UNIT;
  });
  const parts: { source: Decoded; plain: boolean }[] = [];
  let from = 0;
  for (let i = 1; i <= excess.length; i += 1) {
    if (i < excess.length && at(excess, i) === at(excess, from)) continue;
    parts.push({ source: slice(segment, from, i), plain: at(excess, from) });
    from = i;
  }
  return parts;
}

/**
 * S2: the pieces of one segment: the segment whole, or for small caps its case runs: on a face with `smcp`, the
 * letters it doesn't cover drawn synthetic (K1, K7), on a face without, every letter with a capital form (K2).
 */
function pieces(face: ShapingFace, segment: Decoded, smallCaps: boolean, language: string | null): Piece[] {
  if (!smallCaps) return [{ text: encode(segment.codePoints), source: segment, start: at(segment.offsets, 0), features: BASE_FEATURES, scale: 1 }];
  const scale = smallCapsScale(face.metrics);
  if (face.hasSmallCaps) return caseRuns(segment, (codePoint) => hasCapital(face, codePoint) && !smallCapsCovers(face, codePoint, language), { ...BASE_FEATURES, smcp: true }, scale);
  return caseRuns(segment, (codePoint) => hasCapital(face, codePoint), BASE_FEATURES, scale);
}

/**
 * Shapes `text` with `face`: kerning, standard ligatures, true or synthetic small caps, and the language's local
 * forms. Never throws for any text on a face that loaded: a piece the shaper fails on is shaped word by word,
 * and a word it fails on through the character map alone (table F). Text of default ignorables alone gives an
 * empty run.
 */
export function shapeText(face: ShapingFace, text: string, options: ShapeOptions): GlyphRun {
  const decoded = decode(text);
  const language = options.language === null ? null : OPENTYPE_LANGUAGES[options.language];
  const widths = new Map<number, number>();
  const shaped = segments(decoded).flatMap((segment) => {
    const substituted = substitute(face, segment);
    for (const [offset, width] of substituted.widths) widths.set(offset, width);
    return boundedMarks(substituted.source).flatMap((part) =>
      part.plain
        ? [plainPiece(face, { text: encode(part.source.codePoints), source: part.source, start: at(part.source.offsets, 0), features: BASE_FEATURES, scale: 1 })]
        : pieces(face, part.source, options.smallCaps, language).flatMap((piece) => calls(piece).flatMap((call) => shapePiece(face, call, language))),
    );
  });
  const count = shaped.reduce((sum, piece) => sum + piece.run.glyphs.length, 0);
  const run: GlyphRun = {
    glyphIds: new Uint16Array(count),
    clusters: new Uint32Array(count),
    advances: new Float64Array(count),
    xOffsets: new Float64Array(count),
    yOffsets: new Float64Array(count),
    scales: new Float64Array(count),
  };
  let i = 0;
  for (const piece of shaped) {
    piece.run.glyphs.forEach((glyph, k) => {
      const place = at(piece.run.positions, k);
      run.glyphIds[i] = glyph.id;
      run.clusters[i] = i === 0 ? 0 : at(piece.clusters, k);
      // M2: a space the face lacks, drawn as its space glyph, takes the width Unicode gives it.
      const width = glyph.codePoints.length === 1 && glyph.codePoints[0] === 0x20 ? widths.get(at(piece.clusters, k)) : undefined;
      run.advances[i] = (width ?? place.xAdvance) * piece.scale;
      run.xOffsets[i] = place.xOffset * piece.scale;
      run.yOffsets[i] = place.yOffset * piece.scale;
      run.scales[i] = piece.scale;
      i += 1;
    });
  }
  return run;
}

/** A run's total advance, in font units. */
export function runWidth(run: GlyphRun): number {
  return run.advances.reduce((sum, advance) => sum + advance, 0);
}

/**
 * The text glyph `index` stands for, as a UTF-16 range of the `length`-long text the run was shaped from: from
 * its cluster start to the next glyph's cluster start (or the text's end). A ligature's range covers all its
 * characters; the second and later glyphs of one cluster get an empty range at the cluster's end, so the text is
 * given once (as a PDF `ToUnicode` map needs, decision 30).
 */
export function clusterRange(run: GlyphRun, index: number, length: number): { readonly from: number; readonly to: number } {
  const start = at(run.clusters, index);
  let next = index + 1;
  while (next < run.clusters.length && at(run.clusters, next) === start) next += 1;
  const end = next < run.clusters.length ? at(run.clusters, next) : length;
  const first = index === 0 || at(run.clusters, index - 1) !== start;
  return first ? { from: start, to: end } : { from: end, to: end };
}
